// ============================================================================
// FILE: src/admin/pages/operations/OpsRequests.jsx
// ✅ Strict DTO usage (no fallbacks)
// ✅ Simple math on offers:
//    wayrootEarnings = commission + apiCharges
//    ownerNet = fareQuoted - wayrootEarnings - gst
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import OpsRightDrawer from "./components/OpsRightDrawer";
import OpsActionModal from "./components/OpsActionModal";
import {
  opsGetReasons,
  opsListRequests,
  opsGetRequestDetail,
  opsRequestRerun,
  opsAddRequestNote,
  opsAuditLogs,
} from "../../../api/adminOpsApi";

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DT_FMT = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function fmtPickupDT(v) {
  const d = parseDate(v);
  if (!d) return "—";
  const s = DT_FMT.format(d);
  return s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

function fmtDurationFromMins(mins) {
  if (mins == null) return "—";
  const m = Math.max(0, Math.floor(Number(mins)));
  if (m < 60) return `${m}m`;

  const day = Math.floor(m / 1440);
  if (day >= 1) return `${day}d`;

  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${String(mm).padStart(2, "0")}m`;
}

function fmtTimeLeftFromMins(mins) {
  if (mins == null) return "—";
  const n = Math.floor(Number(mins));
  const late = n < 0;
  const abs = Math.abs(n);
  const core = fmtDurationFromMins(abs);
  return late ? `Late ${core}` : core;
}

function fmtMoneyINR(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `₹${n.toLocaleString("en-IN")}`;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function ageMinsFrom(row) {
  const d = parseDate(row?.createdAt) || parseDate(row?.updatedAt);
  if (!d) return null;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  return mins >= 0 ? mins : 0;
}

function timeLeftMins(row) {
  // request has pickupAt (LocalDateTime serialized) OR pickupTime string (legacy)
  const pickup = parseDate(row?.pickupAt) || parseDate(row?.pickupTime);
  if (!pickup) return null;
  return Math.floor((pickup.getTime() - Date.now()) / 60000);
}

function reqBucket(x) {
  const st = String(x?.status || "").toUpperCase();
  const hasBooking = !!x?.finalBookingId;

  if (hasBooking || st.includes("FULFIL")) return "FULFILLED";
  if (st.includes("EXPIRE") || st.includes("CANCEL")) return "EXPIRED";
  return "LIVE";
}

function requestOutcome(x) {
  const bookingStatus = String(x?.finalBookingStatus || "").toUpperCase();
  const rideStatus = String(x?.finalRideStatus || "").toUpperCase();
  const cancelledBy = String(x?.finalCancelledBy || x?.cancelledBy || "").trim();

  if (bookingStatus.includes("CANCEL")) {
    return {
      label: cancelledBy ? `Cancelled by ${cancelledBy}` : "Cancelled later",
      tone: "red",
      detail: x?.finalCancelReason || x?.cancelReason || "Post-booking cancellation",
    };
  }
  if (rideStatus === "IN_PROGRESS" || bookingStatus === "IN_PROGRESS") {
    return { label: "Ride in progress", tone: "violet", detail: "Booking created and trip is underway" };
  }
  if (rideStatus === "ARRIVED_AT_PICKUP" || bookingStatus === "ARRIVED_AT_PICKUP") {
    return { label: "Driver at pickup", tone: "amber", detail: "Booking created and driver reached pickup" };
  }
  if (rideStatus === "ENDED" || bookingStatus.includes("COMPLET")) {
    return { label: "Completed", tone: "green", detail: "Ride finished successfully" };
  }
  if (bookingStatus.includes("BOOK")) {
    return { label: "Booked", tone: "slate", detail: "Booking created and waiting for execution" };
  }
  return {
    label: x?.finalBookingId ? "Booking created" : "Fulfilled",
    tone: "slate",
    detail: x?.finalBookingId ? `Booking #${x.finalBookingId}` : "Matched successfully",
  };
}

function Pill({ tone = "slate", children }) {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
    ink: "bg-slate-900 text-white border-slate-900",
  };
  return (
    <span className={cls("text-[11px] px-2 py-1 rounded-md border font-medium", map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function paymentTone(pay) {
  const s = String(pay || "").toUpperCase();
  if (!s) return "slate";
  if (s.includes("PAID") || s.includes("SUCCESS") || s.includes("CAPTURE")) return "green";
  if (s.includes("PEND") || s.includes("INIT") || s.includes("HOLD")) return "amber";
  if (s.includes("FAIL") || s.includes("REFUND") || s.includes("CANCEL")) return "red";
  return "slate";
}

function timeLeftTone(mins) {
  if (mins == null) return "slate";
  if (mins < 0) return "red";
  if (mins <= 60) return "amber";
  return "slate";
}

function offerTone(status) {
  const s = String(status || "").toUpperCase();
  if (s.includes("ACCEPT")) return "green";
  if (s.includes("DECLIN")) return "red";
  if (s.includes("EXPIRE")) return "amber";
  return "slate";
}

function RowKV({ k, v }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-2 border-b border-slate-200 last:border-b-0">
      <div className="col-span-4 text-xs text-slate-500">{k}</div>
      <div className="col-span-8 text-sm text-slate-900 text-right break-words">{v ?? "—"}</div>
    </div>
  );
}

function computeSla(row) {
  const left = timeLeftMins(row);
  const created = parseDate(row?.createdAt) || parseDate(row?.updatedAt);
  const pickup = parseDate(row?.pickupAt) || parseDate(row?.pickupTime);

  let actionSlaMins = 10;
  if (left != null && left <= 60) actionSlaMins = 5;
  else if (left != null && left <= 360) actionSlaMins = 10;
  else actionSlaMins = 30;

  const actionDeadlineAt = created ? new Date(created.getTime() + actionSlaMins * 60000) : null;
  const hardDeadlineAt = pickup ? new Date(pickup.getTime() - 20 * 60000) : null;

  return { leftMins: left, actionSlaMins, actionDeadlineAt, hardDeadlineAt };
}

function fmtDeadline(d) {
  if (!d) return "—";
  return fmtPickupDT(d.toISOString());
}

export default function OpsRequests() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [rows, setRows] = useState([]);

  const [tab, setTab] = useState("LIVE"); // LIVE / FULFILLED / EXPIRED
  const [selectedId, setSelectedId] = useState(null);

  const [detail, setDetail] = useState(null); // {request, offers, events}
  const [audit, setAudit] = useState([]);

  const [reasons, setReasons] = useState([]);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null); // {type, note?}

  const debounceRef = useRef(null);

  const reasonCodes = useMemo(() => {
    const xs = (reasons || []).map((r) => ({ code: r.code, label: r.label }));
    return xs.length ? xs : [{ code: "SOP_REQUIRED", label: "SOP required action" }];
  }, [reasons]);

  const load = async (queryValue) => {
    setBusy(true);
    setErr("");
    try {
      const res = await opsListRequests({ page: 0, size: 50, q: queryValue ?? q });
      const items = res?.items || [];
      setRows(items);
    } catch (e) {
      setErr(e?.message || "Failed to load requests");
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  const open = async (id) => {
    setSelectedId(id);
    setErr("");
    try {
      const d = await opsGetRequestDetail(id);
      setDetail(d);

      const a = await opsAuditLogs({
        entityType: "BookingMatchRequest",
        entityId: String(id),
        page: 0,
        size: 50,
      });
      setAudit(a?.content || []);
    } catch (e) {
      setDetail(null);
      setAudit([]);
      setErr(e?.message || "Failed to load request detail");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const rr = await opsGetReasons();
        setReasons(rr?.reasons || []);
      } catch {}
      await load("");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q), 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const bucketedRows = useMemo(() => {
    return (rows || []).map((x) => ({
      ...x,
      __bucket: reqBucket(x),
      __ageMins: ageMinsFrom(x),
      __leftMins: timeLeftMins(x),
    }));
  }, [rows]);

  const filteredRows = useMemo(() => bucketedRows.filter((x) => x.__bucket === tab), [bucketedRows, tab]);

  const detailReq = detail?.request;
  const offers = detail?.offers || [];
  const events = detail?.events || [];

  const tabLabel = useMemo(() => (tab === "LIVE" ? "Live" : tab === "FULFILLED" ? "Fulfilled" : "Expired"), [tab]);

  const subtitle = useMemo(() => {
    const r = detailReq;
    if (!r) return "";
    const pickup = fmtPickupDT(r.pickupAt || r.pickupTime);
    if (tab === "LIVE") {
      const sla = computeSla(r);
      return `Pickup: ${pickup} • Time left: ${fmtTimeLeftFromMins(sla.leftMins)} • Offers: ${r.offersTotal ?? 0}`;
    }
    if (tab === "FULFILLED") {
      return `Pickup: ${pickup} • Booking: ${r.finalBookingId ? `#${r.finalBookingId}` : "—"} • Payment: ${
        r.paymentStatus || "—"
      }`;
    }
    return `Pickup: ${pickup} • Payment: ${r.paymentStatus || "—"}`;
  }, [detailReq, tab]);

  const tableHeader = useMemo(() => {
    const base = (
      <>
        <div className="col-span-2">{`Request (${tabLabel})`}</div>
        <div className="col-span-2">Pickup</div>
        <div className="col-span-2">Payment</div>
        <div className="col-span-2">User</div>
        <div className="col-span-2">Plan</div>
      </>
    );

    if (tab === "LIVE") {
      return (
        <>
          {base}
          <div className="col-span-1">Offers</div>
          <div className="col-span-1 text-right">Time left</div>
        </>
      );
    }

    if (tab === "FULFILLED") {
      return (
        <>
          {base}
          <div className="col-span-1">Booking</div>
          <div className="col-span-1 text-right">Outcome</div>
        </>
      );
    }

    return (
      <>
        {base}
        <div className="col-span-2 text-right">Age</div>
      </>
    );
  }, [tab, tabLabel]);

  const closeDrawer = () => {
    setSelectedId(null);
    setDetail(null);
    setAudit([]);
    setModal(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold tracking-wider text-slate-500">Requests</div>
          <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Matching queue</div>
          <div className="mt-1 text-sm text-slate-600">Live requests need action. Fulfilled requests also show what happened after booking creation.</div>
          {err ? <div className="mt-2 text-sm text-red-600">{err}</div> : null}
        </div>

        <div className="flex flex-col gap-3 items-end">
          <div className="flex items-center gap-2">
            {[
              { k: "LIVE", label: "Live" },
              { k: "FULFILLED", label: "Fulfilled" },
              { k: "EXPIRED", label: "Expired" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                className={cls(
                  "h-9 px-4 rounded-md border text-sm font-semibold",
                  tab === t.k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3">
              <span className="text-slate-400 text-sm">⌕</span>
              <input
                className="w-[360px] bg-transparent text-sm outline-none"
                placeholder="Search request id / phone / plan / status"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    clearTimeout(debounceRef.current);
                    load(e.currentTarget.value);
                  }
                }}
              />
              {q ? (
                <button
                  type="button"
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-medium hover:bg-slate-50"
                  onClick={() => setQ("")}
                  title="Clear"
                >
                  Clear
                </button>
              ) : null}
            </div>

            <button
              className="h-10 rounded-md border border-slate-200 px-4 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
              disabled={busy}
              onClick={() => load(q)}
              type="button"
              title="Refresh"
            >
              {busy ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-12 bg-slate-50 px-5 py-3 text-xs font-semibold tracking-wider text-slate-500">
          {tableHeader}
        </div>

        {busy ? (
          <div className="px-5 py-4 text-sm text-slate-600">Loading…</div>
        ) : filteredRows.length ? (
          filteredRows.map((x) => {
            const id = x.requestId;

            const pay = x.paymentStatus || "";

            const left = x.__leftMins;
            const leftTone = timeLeftTone(left);

            const ageText = x.__ageMins != null ? fmtDurationFromMins(x.__ageMins) : "—";
            const bookingId = x.finalBookingId ?? null;
            const outcome = requestOutcome(x);

            return (
              <div
                key={id}
                className={cls(
                  "grid grid-cols-12 px-5 py-3 border-t border-slate-200 text-sm cursor-pointer",
                  selectedId === id ? "bg-slate-50" : "hover:bg-slate-50"
                )}
                onClick={() => open(id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && open(id)}
              >
                <div className="col-span-2 min-w-0">
                  <div className="text-slate-900 font-medium">#{id}</div>
                  {x.terminalReason && tab === "EXPIRED" ? (
                    <div className="mt-1 text-[11px] text-slate-500 truncate" title={x.terminalReason}>
                      {x.terminalReason}
                    </div>
                  ) : null}
                </div>

                <div className="col-span-2 text-[12px] text-slate-700">{fmtPickupDT(x.pickupAt || x.pickupTime)}</div>

                <div className="col-span-2 min-w-0">
                  <div className="flex items-center gap-2">
                    <Pill tone={paymentTone(pay)}>{pay || "—"}</Pill>
                    
                  </div>
                </div>
                <div className="col-span-2 text-[12px] text-slate-700 truncate">{x.userPhone || "—"}</div>

                <div className="col-span-2 text-[12px] text-slate-900 truncate">{x.planKey || "—"}</div>

                {tab === "LIVE" ? (
                  <>
                    <div className="col-span-1 text-[12px] text-slate-900">{x.offersTotal ?? 0}</div>
                    <div className="col-span-1 text-right">
                      <Pill tone={leftTone}>{fmtTimeLeftFromMins(left)}</Pill>
                    </div>
                  </>
                ) : tab === "FULFILLED" ? (
                  <>
                    <div className="col-span-1 text-[12px] text-slate-900">{bookingId ? `#${bookingId}` : "—"}</div>
                    <div className="col-span-1 text-right">
                      <Pill tone={outcome.tone}>{outcome.label}</Pill>
                    </div>
                  </>
                ) : (
                  <div className="col-span-2 text-right text-[12px] text-slate-600">{ageText}</div>
                )}
              </div>
            );
          })
        ) : (
          <div className="px-5 py-4 text-sm text-slate-600">No requests found.</div>
        )}
      </div>

      {/* Drawer */}
      <OpsRightDrawer
        open={!!selectedId}
        title={selectedId ? `Request #${selectedId}` : "Request"}
        subtitle={subtitle}
        onClose={closeDrawer}
        width={820}
      >
        <div className="p-5 space-y-4">
          {!detailReq ? (
            <div className="text-sm text-slate-600">No details loaded.</div>
          ) : (
            <>
              {(() => {
                const bucket = reqBucket(detailReq);
                const sla = computeSla(detailReq);
                const age = ageMinsFrom(detailReq);

                const payment = detailReq.paymentStatus || "—";
                const bookingId = detailReq.finalBookingId ?? null;
                const outcome = requestOutcome(detailReq);

                const pickupAt = detailReq.pickupAt || detailReq.pickupTime;
                const dropAt = detailReq.dropAt || detailReq.dropTime;

                const durationMins =
                  detailReq.durationHours != null ? Math.round(num(detailReq.durationHours) * 60) : null;

                const stops =
                  Array.isArray(detailReq.destinations) && detailReq.destinations.length
                    ? detailReq.destinations.join(" → ")
                    : "—";

                // request level computed (same formula)
                const reqWayroot = num(detailReq.commission) + num(detailReq.apiCharges);
                const reqOwnerNet = num(detailReq.fare) - reqWayroot - num(detailReq.gst);

                return (
                  <>
                    {/* Operations */}
                    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">Operations</div>
                          <div className="text-xs text-slate-500">Actions are audited with reason</div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-semibold"
                            onClick={() => setModal({ type: "rerun" })}
                            type="button"
                          >
                            Run matching again
                          </button>

                          <button
                            className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                            onClick={() => setModal({ type: "note", note: "" })}
                            type="button"
                          >
                            Add internal note
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Summary + Customer */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50">
                          <div className="text-sm font-semibold text-slate-900">Request details</div>
                        </div>
                        <div className="px-4">
                          <RowKV k="Bucket" v={bucket} />
                          <RowKV k="Request ID" v={selectedId ? `#${selectedId}` : "—"} />
                          <RowKV k="Pickup" v={fmtPickupDT(pickupAt)} />
                          <RowKV k="Payment" v={<Pill tone={paymentTone(payment)}>{String(payment)}</Pill>} />
                          <RowKV k="Plan" v={detailReq.planKey || "—"} />
                          <RowKV k="Age" v={age != null ? fmtDurationFromMins(age) : "—"} />

                          {bucket === "LIVE" ? (
                            <>
                              <RowKV
                                k="Time left"
                                v={<Pill tone={timeLeftTone(sla.leftMins)}>{fmtTimeLeftFromMins(sla.leftMins)}</Pill>}
                              />
                              <RowKV k="Offers" v={detailReq.offersTotal ?? 0} />
                              <RowKV k="Action SLA" v={`${sla.actionSlaMins}m`} />
                              <RowKV k="Action deadline" v={fmtDeadline(sla.actionDeadlineAt)} />
                            </>
                          ) : bucket === "FULFILLED" ? (
                            <>
                              <RowKV k="Booking ID" v={bookingId ? `#${bookingId}` : "—"} />
                              <RowKV k="Outcome" v={<Pill tone={outcome.tone}>{outcome.label}</Pill>} />
                              <RowKV k="Ride / booking status" v={detailReq.finalRideStatus || detailReq.finalBookingStatus || "—"} />
                              {(detailReq.finalCancelledBy || detailReq.finalCancelReason) ? (
                                <RowKV
                                  k="Cancellation"
                                  v={`${detailReq.finalCancelledBy || "Unknown"}${detailReq.finalCancelReason ? ` • ${detailReq.finalCancelReason}` : ""}`}
                                />
                              ) : null}
                            </>
                          ) : null}

                          {bucket === "EXPIRED" ? (
                            <>
                              {detailReq.terminalReason ? <RowKV k="Terminal reason" v={detailReq.terminalReason} /> : null}
                              {(detailReq.cancelledBy || detailReq.cancelReason) ? (
                                <RowKV
                                  k="Cancelled by"
                                  v={`${detailReq.cancelledBy || "Unknown"}${detailReq.cancelReason ? ` • ${detailReq.cancelReason}` : ""}`}
                                />
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50">
                          <div className="text-sm font-semibold text-slate-900">Customer</div>
                        </div>
                        <div className="px-4">
                          <RowKV k="Name" v={detailReq.userName || "—"} />
                          <RowKV k="Phone" v={detailReq.userPhone || "—"} />
                          <RowKV k="Email" v={detailReq.userEmail || "—"} />
                          <RowKV k="User ID" v={detailReq.userId ?? "—"} />
                        </div>
                      </div>
                    </div>

                    {/* Trip + Pricing */}
                    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50">
                        <div className="text-sm font-semibold text-slate-900">Trip & pricing</div>
                      </div>
                      <div className="px-4">
                        <RowKV k="Pickup location" v={detailReq.pickupLocation || "—"} />
                        <RowKV k="Stops" v={stops} />
                        <RowKV k="Destination" v={detailReq.destination || "—"} />

                        <RowKV k="Schedule • Pickup" v={fmtPickupDT(pickupAt)} />
                        <RowKV k="Schedule • Drop" v={dropAt ? fmtPickupDT(dropAt) : "—"} />

                        <RowKV
                          k="Distance"
                          v={detailReq.travelDistance != null ? `${num(detailReq.travelDistance).toFixed(1)} km` : "—"}
                        />
                        <RowKV k="Duration" v={durationMins != null ? fmtDurationFromMins(durationMins) : "—"} />

                        <RowKV k="Fare" v={fmtMoneyINR(detailReq.fare)} />
                        <RowKV k="Paid amount" v={fmtMoneyINR(detailReq.paidAmount)} />
                        <RowKV k="Refunded" v={fmtMoneyINR(detailReq.refundedAmount)} />
                        <RowKV k="Pending refund" v={fmtMoneyINR(detailReq.pendingRefundAmount)} />

                        <RowKV k="Wayroot earnings" v={fmtMoneyINR(reqWayroot)} />
                        <RowKV k="Owner net" v={fmtMoneyINR(reqOwnerNet)} />

                        
                        <RowKV k="GST" v={fmtMoneyINR(detailReq.gst)} />

                        <RowKV k="Batch" v={detailReq.matchBatchNo ?? "—"} />
                        <RowKV k="Final booking" v={detailReq.finalBookingId ? `#${detailReq.finalBookingId}` : "—"} />
                      </div>
                    </div>

          {/* Offers (LIVE only) */}
{bucket === "LIVE" ? (
  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
    <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
      <div className="text-sm font-semibold text-slate-900">Offers</div>
      <div className="text-xs text-slate-500">
        Total {detailReq.offersTotal ?? 0} • Pending {detailReq.offersPending ?? 0} • Accepted{" "}
        {detailReq.offersAccepted ?? 0} • Declined {detailReq.offersDeclined ?? 0} • Expired{" "}
        {detailReq.offersExpired ?? 0}
      </div>
    </div>

    {!offers.length ? (
      <div className="px-4 py-4 text-sm text-slate-600">No offers yet.</div>
    ) : (
      <div className="px-4 py-4 overflow-hidden">
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-12 bg-slate-50 px-4 py-2 text-xs font-semibold tracking-wider text-slate-500">
            <div className="col-span-3">Vehicle</div>
            <div className="col-span-3">Owner</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Batch</div>
            <div className="col-span-1 text-right">Fare</div>
            <div className="col-span-2 text-right">Owner net</div>
          </div>

          {offers.map((o) => {
            const fare = Number(o?.fareQuoted ?? 0) || 0;

            // ✅ Owner net = fare - commission - apiCharges - gst (clamped to >= 0)
            const ownerNetRaw =
              fare -
              (Number(o?.commission ?? 0) || 0) -
              (Number(o?.apiCharges ?? 0) || 0) -
              (Number(o?.gst ?? 0) || 0);
            const ownerNet = Math.max(0, ownerNetRaw);

            const status = String(o?.status || "").toUpperCase();
            const reason =
              status === "EXPIRED"
                ? (o?.expiredReason || o?.note || "Expired")
                : (o?.note || "");

            return (
              <div key={String(o.matchId)} className="grid grid-cols-12 px-4 py-3 border-t border-slate-200">
                <div className="col-span-3 text-[12px] text-slate-900 truncate">
                  {o?.vehicle?.registrationNumber || "—"}
                </div>

                <div className="col-span-3 text-[12px] text-slate-700 truncate">
                  {(o?.ownerName || "—") + (o?.ownerPhone ? ` • ${o.ownerPhone}` : "")}
                </div>

                <div className="col-span-2 min-w-0">
                  <Pill tone={offerTone(o.status)}>{o.status || "—"}</Pill>
                  {reason ? (
                    <div className="mt-1 text-[11px] text-slate-500 truncate" title={reason}>
                      {reason}
                    </div>
                  ) : null}
                </div>

                <div className="col-span-1 text-[12px] text-slate-700">{o?.batchNo ?? "—"}</div>

                <div className="col-span-1 text-right text-[12px] text-slate-700">
                  {fmtMoneyINR(fare)}
                </div>

                <div className="col-span-2 text-right">
                  <div className="text-[12px] text-slate-900">{fmtMoneyINR(ownerNet)}</div>
                  
                </div>
              </div>
            );
          })}
        </div>

        
      </div>
    )}
  </div>
) : null}



                    {/* Events */}
                    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50">
                        <div className="text-sm font-semibold text-slate-900">Events</div>
                      </div>
                      <div className="px-4 py-4 space-y-2">
                        {events?.length ? (
                          events.slice(0, 15).map((e) => (
                            <div key={String(e.id)} className="rounded-lg border border-slate-200 p-3">
                              <div className="text-xs text-slate-500">
                                {fmtPickupDT(e.createdAt)} • {e.eventType} • {e.actorType}
                                {e.actorId ? `:${e.actorId}` : ""}
                              </div>
                              <div className="mt-1 text-sm text-slate-700">{e.message || "—"}</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-slate-600">No events.</div>
                        )}
                      </div>
                    </div>

                    {/* Audit */}
                    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50">
                        <div className="text-sm font-semibold text-slate-900">Audit</div>
                      </div>
                      <div className="px-4 py-4 space-y-2">
                        {audit?.length ? (
                          audit.slice(0, 12).map((a) => (
                            <div key={a.id} className="rounded-lg border border-slate-200 p-3">
                              <div className="text-xs text-slate-500">
                                {fmtPickupDT(a.createdAt)} • {a.actionKey}
                              </div>
                              <div className="mt-1 text-sm text-slate-700">{a.reason || "—"}</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-slate-600">No audit logs.</div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </OpsRightDrawer>

      {/* Action modal */}
      <OpsActionModal
        open={!!modal}
        title={modal?.type === "rerun" ? "Run matching again?" : "Add internal note"}
        hint="Choose a reason so future admins know why this was done."
        reasons={reasonCodes}
        confirmLabel={modal?.type === "note" ? "Save note" : "Confirm"}
        onClose={() => setModal(null)}
        onConfirm={async (reason) => {
          if (!selectedId) return;
          if (modal?.type === "rerun") await opsRequestRerun(selectedId, reason);
          if (modal?.type === "note") await opsAddRequestNote(selectedId, modal.note || "", [], reason);
          await open(selectedId);
        }}
      >
        {modal?.type === "note" ? (
          <div>
            <div className="text-sm text-slate-700">Note</div>
            <textarea
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[90px]"
              value={modal.note || ""}
              onChange={(e) => setModal({ ...modal, note: e.target.value })}
              placeholder="Write what happened + next step…"
            />
          </div>
        ) : null}
      </OpsActionModal>
    </div>
  );
}
